/**
 * The mail composer's mode contract — one row per mode, and nothing branching
 * on the mode outside this table.
 *
 * #623 asks that a mode needing more be handled by WIDENING this table rather
 * than forking the composer, so the contract lives here as data, not as
 * conditionals scattered through the component. A new mode is a new row.
 *
 * | field            | meaning                                                      |
 * |------------------|--------------------------------------------------------------|
 * | `header`         | the line above the composer, or null for none                 |
 * | `placeholder`    | the editor's empty-state text                                 |
 * | `sendLabel`      | the Send button's text                                        |
 * | `threadBehaviour`| `'in-thread'` the reply lands on this thread · `'new-thread'`  |
 * | `annotation`     | the short truth shown beside the header, or null              |
 * | `anchored`       | true when the composer opens anchored to one message          |
 * | `recipientModel` | `'picker'` empty + editable · `'chips'` prefilled + editable · |
 * |                  | `'implicit'` no recipient row at all (the caller resolves To) |
 * | `subjectTemplate`| `null` no subject row (the thread's) · `''` starts empty ·    |
 * |                  | `'Re: {subject}'` derived by the CALLER via `threadSubject`   |
 * | `subjectEditable`| true: an input · false: a read-only line (the prototype's      |
 * |                  | thread-level card shows "Re: …" as text, not a field)         |
 *
 * Consumers never read `recipientModel` / `subjectTemplate` / `subjectEditable`
 * directly. They read `showsRecipients`, `showsSubject` and `subjectMode` below,
 * so the rule for each lives in one place.
 *
 * `subjectMode` doubles as `TemplateSignatureSelectors`' `subjectMode`: a
 * template may write the subject only when the user can see AND edit it. A
 * template writing into a subject the user cannot see, or cannot change back,
 * is a silent change to a message that really goes out.
 *
 * ## History: the render enums are gone (#624 → #659)
 *
 * Until #659 each row also carried `recipients` (`'picker'` | `'implicit'`) and
 * `subject` (`'fill'` | `'ignore'`) as RENDER enums, with the threading model
 * parked beside them in `recipientModel` / `subjectTemplate` because the two
 * names were already taken by live consumers. #659 built the implicit reply and
 * cut `MailComposer` over to the new keys, deleting the render enums in the same
 * change. Do not reintroduce a second key that decides the same row.
 * `subjectEditable` came with it: the prototype's thread-level card SHOWS its
 * derived subject without offering it for editing.
 *
 * ## The headers are load-bearing text
 *
 * `header` and `annotation` are the product's explanation of the threading
 * model to the user: a reply STAYS on the thread, reply-all and forward start a
 * NEW one. `threadBehaviour` is the same fact in machine-readable form, and the
 * two must never disagree — that is what makes "New thread" a promise rather
 * than decoration. Do not paraphrase these strings to fit a layout.
 */
export const COMPOSER_MODES = {
    compose: {
        header: null,
        placeholder: 'Write your email…',
        sendLabel: 'Send',
        threadBehaviour: 'new-thread',
        annotation: null,
        anchored: false,
        recipientModel: 'picker',
        // '' and not null: null now means "no subject row", and compose is the
        // one mode whose subject the user always writes.
        subjectTemplate: '',
        subjectEditable: true,
    },
    reply: {
        header: 'Reply to {name}',
        placeholder: 'Write your reply…',
        sendLabel: 'Send reply',
        threadBehaviour: 'in-thread',
        annotation: 'stays in this thread',
        anchored: true,
        recipientModel: 'implicit',
        subjectTemplate: null,
        subjectEditable: false,
    },
    reply_all: {
        header: 'Reply all — everyone on this thread',
        placeholder: 'Write to everyone on this thread…',
        sendLabel: 'Send',
        threadBehaviour: 'new-thread',
        annotation: 'New thread',
        anchored: false,
        recipientModel: 'chips',
        // The prototype's thread-level card: "Re: " + the thread subject, shown
        // as a read-only line. It is TRUE since #659 — both lanes send reply-all
        // through their COMPOSE endpoint, with no In-Reply-To/References, so it
        // really starts a new thread and this is really its subject.
        subjectTemplate: 'Re: {subject}',
        subjectEditable: false,
    },
    forward: {
        header: 'Forward entire thread ({n} messages)',
        placeholder: 'Add a note above the forwarded thread…',
        sendLabel: 'Send forward',
        threadBehaviour: 'new-thread',
        annotation: 'New thread',
        anchored: false,
        // 'picker', not 'chips': a forward is never prefilled (#624, and the
        // prototype's `openForwardAll: to: []`). No inline surface mounts this
        // mode today; both Forward actions navigate to the compose page.
        recipientModel: 'picker',
        subjectTemplate: 'Fwd: {subject}',
        // Read-only, as the prototype's thread-level forward card draws it.
        subjectEditable: false,
    },
};

/** True unless the mode's recipients are implicit (no To/Cc/Bcc rows at all). */
export const showsRecipients = contract => contract.recipientModel !== 'implicit';

/** True when the mode draws a subject row at all (editable or read-only). */
export const showsSubject = contract => contract.subjectTemplate !== null;

/**
 * `'fill'` when the subject is an input a template may populate, `'ignore'`
 * otherwise (no row, or a read-only one). The input and a template's
 * permission to write into it both come from here.
 */
export const subjectMode = contract => (
    showsSubject(contract) && contract.subjectEditable ? 'fill' : 'ignore'
);

/** Leading `Re:` / `Fwd:` / `Fw:` prefixes, repeated or mixed, any case. */
const SUBJECT_PREFIX_RE = /^(\s*(re|fwd?)\s*:\s*)+/i;

/**
 * The subject a mode's message goes out with, from the thread's subject:
 * prefixes stripped (one or several, as the prototype strips one), then the
 * mode's template applied. Deliberately, a "Fwd: X" thread's reply-all is
 * "Re: X": the prototype's `R.subject.replace(/^(Re|Fwd): /, "")` does the
 * same, and a reply-all is a reply, not a forward. Reply-all to
 * "Re: Re: Renewal" is "Re: Renewal", never "Re: Re: Re: Renewal". The lanes
 * call this ONCE and pass the result both to the composer (which shows it)
 * and to the send payload, so what is shown is what is sent.
 */
export function threadSubject(contract, subject) {
    if (!showsSubject(contract)) return null;
    const base = String(subject || '').replace(SUBJECT_PREFIX_RE, '').trim();
    // An empty thread subject gives the bare prefix ("Re:"), never a
    // placeholder like "(No subject)" written into a real outgoing subject.
    // (formatModeText would leave an empty token as a literal "{subject}".)
    if (!base) return contract.subjectTemplate.replace('{subject}', '').trim();
    return formatModeText(contract.subjectTemplate, { subject: base });
}

/** Falls back to `compose` so an unknown mode degrades to the most permissive. */
export function modeContract(mode) {
    return COMPOSER_MODES[mode] || COMPOSER_MODES.compose;
}

/**
 * Fill the `{name}` / `{subject}` / `{n}` tokens in a `header` or
 * `subjectTemplate`.
 *
 * A token with no value is left in place, literally. That is deliberate: an
 * unfilled `{n}` is ugly and immediately visible in the UI, where dropping it
 * would ship "Forward entire thread ()" — a confident, wrong statement about
 * what the user is about to send. Loud beats tidy; the #596 lesson applies to
 * copy as much as to payloads.
 *
 * Returns null for a null template, so `formatModeText(contract.header)` on
 * `compose` stays "no header" rather than the string "null".
 */
export function formatModeText(template, vars = {}) {
    if (template == null) return null;
    return String(template).replace(/\{(\w+)\}/g, (token, key) => (
        vars[key] == null || vars[key] === '' ? token : String(vars[key])
    ));
}
